import { COMMON_RESOURCES } from '@dl/declarations';
import clsx from 'clsx';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { initialValues, RoleFormData, schema } from '../../utils/forms/role';
import { RolesPath } from '../../utils/routes';
import { Button } from '../data/Button';
import { SystemCheckbox } from '../data/Checkbox';
import { ControlledSystemTextInput } from '../data/TextInput';
import { FormContainer } from './FormContainer';

const ACTION_LABEL = {
    c: 'Create',
    r: 'Read',
    u: 'Update',
    d: 'Delete'
}

export const RoleForm = () => {
    return (
        <FormContainer<RoleFormData>
            title='Role'
            headerOptions={{
                show: false
            }}
            queryOptions={{
                enabled: true,
                basePath: RolesPath
            }}
            schema={schema}
            defaultValues={initialValues}
            deleteOptions={{
                show: true
            }}
        >
            <Form />
        </FormContainer>
    )
}

const Form = () => {
    const { control, watch } = useFormContext();

    const permissions: string[] = watch("permissions");

    const {
        replace
    } = useFieldArray({
        control,
        name: "permissions"
    })

    const toggleResourcePermission = (resource: string, action: string) => {
        const currentPermission = permissions.find((p) => p.startsWith(resource + ":")) || `${resource}:`
        const [currentResource, currentActions] = currentPermission.split(':')

        let newPermissions: string[] = []
        const hasAction = currentActions.includes(action)

        if (hasAction) {
            newPermissions = permissions.map((p) => {
                if (p.startsWith(currentResource + ":")) {
                    const updatedActions = currentActions.replace(action, '')
                    return `${currentResource}:${updatedActions}`
                }

                return p
            })
        } else {
            newPermissions = permissions.map((p) => {
                if (p.startsWith(currentResource + ":")) {
                    return `${currentResource}:${currentActions + action}`
                }

                return p
            })
        }

        replace(newPermissions);
    };

    const toggleAllResourcePermissions = (resource: string) => {
        const currentPermission = permissions?.find((p) => p.startsWith(resource + ":"))

        if (!currentPermission) {
            replace([...permissions, `${resource}:crud`])
            return
        }

        const [currentResource, currentActions] = currentPermission.split(':')

        const hasAnyAction = /^[crud]+$/.test(currentActions)

        let newPermissions: string[] = []

        if (hasAnyAction) {
            newPermissions = permissions.map((p) => {
                if (p.startsWith(currentResource + ":")) {
                    return `${currentResource}:`
                }

                return p
            })
        }
        else {
            newPermissions = permissions.map((p) => {
                if (p.startsWith(currentResource + ":")) {
                    return `${currentResource}:crud`
                }

                return p
            })
        }

        replace(newPermissions)
    };

    const isResourcePermissionChecked = (resource: string, action: string) => {
        const currentPermission = permissions?.find((p) => p.startsWith(resource + ":"));
        if (!currentPermission) return false;

        const [_currentResource, currentActions] = currentPermission.split(':')
        return currentActions.includes(action);
    };

    return (
        <div className='flex flex-col items-center md:grid md:grid-cols-2 md:items-start gap-4'>
            <div className='col-span-full justify-self-start'>
                <ControlledSystemTextInput
                    label='Name'
                    inputSize='full'
                    name='name'
                />
            </div>

            <hr className='col-span-full' />

            <div className='col-span-full flex flex-col gap-1'>
                {Object.values(COMMON_RESOURCES)
                    .map((resource) => (
                        <div
                            key={resource.ROUTE}
                            className={clsx(
                                'my-1 grid grid-cols-8 gap-1',
                                resource.CMS.SHOW_ROLE ? '' : 'hidden'
                            )}
                        >
                            <h3 className='first-letter:uppercase col-span-2'>{resource.CMS.TITLE}</h3>

                            <div className='col-span-2'>
                                <Button
                                    type='button'
                                    onClick={() => toggleAllResourcePermissions(resource.ROUTE)}
                                >
                                    Toggle all
                                </Button>
                            </div>

                            {['c', 'r', 'u', 'd'].map((action) => (
                                <div
                                    key={`${resource}-${action}`}
                                    className='flex items-center col-span-1'
                                >
                                    <SystemCheckbox
                                        label={ACTION_LABEL[action as keyof typeof ACTION_LABEL]}
                                        checked={isResourcePermissionChecked(resource.ROUTE, action)}
                                        handleChange={() => toggleResourcePermission(resource.ROUTE, action)}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
            </div>
        </div>
    )
}